import { ComponentType, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { getEnabledOAuthProviders, OAuthProviderId } from "@/config/oauth.config";
import { GoogleLogo, MicrosoftLogo } from "@/components/ui/provider-logos";

const LOGOS: Record<OAuthProviderId, ComponentType<{ className?: string }>> = {
  google: GoogleLogo,
  azure: MicrosoftLogo,
};

/**
 * Provider sign-in buttons for the email step of the login screen.
 *
 * Renders nothing when no provider is enabled, so with the flags unset the
 * sign-in screen is byte-for-byte the pre-AE-206 experience. Brand marks sit on
 * a white bordered surface because both Google's and Microsoft's brand
 * guidelines expect the mark on a neutral background, not on our teal primary.
 */
export const OAuthSignInButtons = ({ disabled = false }: { disabled?: boolean }) => {
  const providers = getEnabledOAuthProviders();
  const [pendingProvider, setPendingProvider] = useState<OAuthProviderId | null>(null);
  const { signInWithOAuth } = useAuth();
  const { toast } = useToast();

  if (providers.length === 0) return null;

  const handleClick = async (id: OAuthProviderId) => {
    setPendingProvider(id);
    try {
      const { error } = await signInWithOAuth(id);
      if (error) {
        toast({ title: "Sign-in failed", description: error.message, variant: "destructive" });
        setPendingProvider(null);
      }
      // On success the browser is navigating away — leave the button pending.
    } catch (error) {
      Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
        event.severity = "error";
        event.context = "login.signInWithOAuth";
      });
      toast({ title: "Sign-in failed", description: "Something went wrong", variant: "destructive" });
      setPendingProvider(null);
    }
  };

  return (
    <div className="space-y-3">
      {providers.map(({ id, label }) => {
        const Logo = LOGOS[id];
        return (
          <button
            key={id}
            type="button"
            onClick={() => void handleClick(id)}
            disabled={disabled || pendingProvider !== null}
            className="flex w-full items-center justify-center gap-3 rounded-lg border border-input bg-white px-4 py-2.5 text-sm font-medium text-bodhi-text-dark transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Logo className="h-5 w-5" />
            <span>{pendingProvider === id ? "Redirecting..." : label}</span>
          </button>
        );
      })}

      <div className="flex items-center gap-3 pt-1">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground">or continue with email</span>
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
};
